import { Block, DocHeader, Grid, SignatureRow, staffName } from "@/components/PrintDoc";
import { inr, dmy, dmyTime, titleize } from "@/lib/format";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-600">{label}</span><span>{value}</span></div>
);

const address = (value) => Object.values(value || {}).filter(Boolean).join(", ") || "—";

export function BirthRecordDoc({ record, business = null }) {
  if (!record) return null;
  return (
    <div data-testid="birth-record-print-document">
      <DocHeader
        business={business}
        docTitle="Birth Record — Form No. 1"
        right={<p className="text-[11px]">Record No: <b>{record.record_no || "—"}</b></p>}
      />
      <Grid rows={[
        ["Child's name", record.child?.name],
        ["Date of birth", record.date_of_birth],
        ["Sex", record.sex],
        ["Father's name", record.father?.name],
        ["Mother's name", record.mother?.name],
        ["Father's mobile", record.father?.mobile_no],
        ["Mother's mobile", record.mother?.mobile_no],
        ["Place of birth", record.place_of_birth?.institution_name || record.place_of_birth?.type],
        ["Registration no.", record.registrar?.registration_no],
      ]} />
      <Block title="Address of parents at birth">{address(record.address_at_birth)}</Block>
      <Block title="Permanent address">{address(record.permanent_address)}</Block>
      <Block title="Informant">{record.informant?.name || "—"}</Block>
      <Block title="Registrar remarks">{record.registrar?.remarks || "—"}</Block>
      <SignatureRow label="Authorised signatory" extra="Government of India Form No. 1 record." />
    </div>
  );
}

export function BirthPaymentReceiptDoc({ receipt }) {
  const { invoice, birth, patient, business } = receipt || {};
  if (!invoice || !birth) return null;
  const payments = (invoice.payments || []).filter((p) => String((p.kind || "payment")).toLowerCase() !== "refund");
  const totalPaid = round2(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
  const total = round2(Number(invoice.total || 0));
  const outstanding = round2(Math.max(total - totalPaid, 0));
  const paidAt = payments.length ? payments[payments.length - 1].at || invoice.created_at : invoice.created_at;
  return (
    <div data-testid="birth-payment-receipt-document">
      <DocHeader business={business} docTitle="Payment Receipt"
        right={<>
          <p className="text-[11px]">Receipt No: <b>{invoice.invoice_no}</b></p>
          <p className="text-[11px]">Record No: <b>{birth.record_no}</b></p>
          <p className="text-[11px]">{dmyTime(paidAt)}</p>
          <p className="text-[11px] uppercase">Status: <b>{titleize(invoice.status)}</b></p>
        </>} />
      <Grid cols={3} rows={[
        ["Mother", birth.mother?.name || (patient && patient.name) || "—"],
        ["Child", birth.child?.name || "—"],
        ["Date of birth", birth.date_of_birth || "—"],
        ["UHID", patient?.uhid || birth.uhid || "—"],
        ["Mobile", patient?.phone || birth.mother?.mobile_no || birth.father?.mobile_no || "—"],
        ["Invoice type", titleize(invoice.invoice_type)],
        ["Reference", invoice.reference_no || birth.record_no || "—"],
        ["Bill date", dmy(invoice.created_at)],
      ]} />
      <div className="mt-5">
        <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Payment method breakdown</p>
        {payments.length === 0 ? (
          <p className="mt-2 text-slate-400">No payments recorded yet.</p>
        ) : (
          <table className="mt-2 w-full border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-300 text-left">
                <th className="py-1.5">Date & time</th>
                <th className="py-1.5">Mode</th>
                <th className="py-1.5 text-right">Amount</th>
                <th className="py-1.5 text-right">Reference</th>
                <th className="py-1.5">Collected by</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_id} className="border-b border-slate-100 align-top">
                  <td className="py-1.5 whitespace-nowrap">{dmyTime(p.at || invoice.created_at)}</td>
                  <td className="py-1.5">{titleize(p.mode)}</td>
                  <td className="py-1.5 text-right">{inr(Number(p.amount || 0))}</td>
                  <td className="py-1.5">{p.reference_no || "—"}</td>
                  <td className="py-1.5">{staffName(p)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-800 font-bold">
                <td className="py-1.5" colSpan={2}>Total paid</td>
                <td className="py-1.5 text-right">{inr(totalPaid)}</td>
                <td className="py-1.5"></td>
                <td className="py-1.5"></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <div className="w-72 space-y-1 text-[12px]">
          <Row label="Charge amount" value={inr(Number(birth.birth_charge || 0))} />
          <Row label="Total paid" value={inr(totalPaid)} />
          <div className="flex justify-between border-y-2 border-slate-800 py-1.5 font-ui text-[14px] font-bold">
            <span>Outstanding balance</span><span>{inr(outstanding)}</span>
          </div>
        </div>
      </div>
      {invoice.notes && <Block title="Notes">{invoice.notes}</Block>}
      <SignatureRow label="Authorised signatory"
        extra="Birth record payment receipt — Government of India Form No. 1 record." />
    </div>
  );
}

export function DeathRecordDoc({ record, business = null }) {
  if (!record) return null;
  return (
    <div data-testid="death-record-print-document">
      <DocHeader
        business={business}
        docTitle="Death Record — Form No. 2"
        right={<p className="text-[11px]">Record No: <b>{record.record_no || "—"}</b></p>}
      />
      <Grid rows={[
        ["Name of deceased", record.deceased?.name],
        ["Date of death", record.date_of_death],
        ["Sex", record.sex],
        ["Age", record.deceased?.age],
        ["Father's name", record.father?.name],
        ["Mother's name", record.mother?.name],
        ["Informant", record.informant?.name],
        ["Place of death", record.place_of_death?.institution_name || record.place_of_death?.type],
        ["Registration no.", record.registrar?.registration_no],
      ]} />
      <Block title="Address at death">{address(record.address_at_death)}</Block>
      <Block title="Permanent address">{address(record.permanent_address)}</Block>
      <Block title="Cause of death (legal record)">{record.cause_of_death_name || "—"}</Block>
      <Block title="Registrar remarks">{record.registrar?.remarks || "—"}</Block>
      <SignatureRow label="Authorised signatory" extra="Government of India Form No. 2 record." />
    </div>
  );
}